import { convertToTimezone } from "@gigpro/utils/datetime";
import type { HiringScheduleItem } from "@sidegigllc/api/client";
import { addWeeks, isBefore, startOfDay } from "date-fns";
import type { MarketplaceSearch } from "./useMarketplaceSearch";

/** The hiring-schedule feed is asked for at most this many weeks at a time. */
export const MAX_FEED_WEEKS = 7;

/** The list view shows interviews from today for this many weeks, two feed chunks, whatever the gig list has paged through. */
export const LIST_FEED_WEEKS = 2 * MAX_FEED_WEEKS;

export type Range = { start: Date; end: Date };

/** The window the Schedule list draws interviews from: the same feed the calendar reads, asked for from today. */
export const listFeedRange = (now: Date): Range => {
  const start = startOfDay(now);
  return { start, end: addWeeks(start, LIST_FEED_WEEKS) };
};

/** Splits a window into back-to-back ranges of at most `maxWeeks` weeks. */
export const chunkRange = ({ start, end }: Range, maxWeeks = MAX_FEED_WEEKS): Array<Range> => {
  const chunks: Array<Range> = [];
  let cursor = start;
  while (isBefore(cursor, end)) {
    const next = addWeeks(cursor, maxWeeks);
    const chunkEnd = isBefore(next, end) ? next : end;
    chunks.push({ start: cursor, end: chunkEnd });
    cursor = chunkEnd;
  }
  return chunks.length > 0 ? chunks : [{ start, end }];
};

/** Chunks share their boundary instant, so an interview starting on it comes back twice. */
export const uniqueById = <T extends { id: string }>(items: Array<T>): Array<T> => [
  ...new Map(items.map((item) => [item.id, item])).values(),
];

/** Unset (an old saved URL) shows every interview type. */
export const isInterviewShownByFilter = (
  interview: Pick<HiringScheduleItem, "type">,
  interviews: MarketplaceSearch["interviews"],
): boolean => interviews?.includes(interview.type) ?? true;

/** Decision 15: a working-interview row is dropped only when its shift is among the gigs the calendar loaded, so it never shows twice. A row whose shift was not loaded, and a legacy row with no shift, still render. */
export const isShiftBackedInterview = (interview: HiringScheduleItem, loadedGigIds: ReadonlySet<number>): boolean =>
  "gig_id" in interview && interview.gig_id != null && loadedGigIds.has(interview.gig_id);

/**
 * The feed rows the week grid draws. `loadedGigIds` is every gig the calendar loaded, before the Interviews
 * filter hides any, so a hidden shift is not brought back as an interview row. Undefined until the gigs load,
 * which holds the rows back rather than flashing shift-backed ones.
 */
export const calendarInterviews = (
  feed: Array<HiringScheduleItem> | undefined,
  interviews: MarketplaceSearch["interviews"],
  loadedGigIds: ReadonlySet<number> | undefined,
): Array<HiringScheduleItem> =>
  loadedGigIds
    ? (feed ?? []).filter(
        (interview) =>
          isInterviewShownByFilter(interview, interviews) && !isShiftBackedInterview(interview, loadedGigIds),
      )
    : [];

/** `interview_id` opens the panel only for an interview the feed holds, so a stale or foreign id leaves it closed. */
export const openableInterviewId = (feed: Array<Pick<HiringScheduleItem, "id">> | undefined, id: string | undefined) =>
  feed?.some((interview) => interview.id === id) ? id : undefined;

/**
 * The feed rows the Schedule list draws: the calendar's rows, minus any that already ended. Feed times are zoned
 * into the posting's timezone, so "now" is compared in that zone too.
 */
export const listInterviews = (
  feed: Array<HiringScheduleItem> | undefined,
  interviews: MarketplaceSearch["interviews"],
  loadedGigIds: ReadonlySet<number> | undefined,
  now: Date,
): Array<HiringScheduleItem> =>
  calendarInterviews(feed, interviews, loadedGigIds).filter(
    (interview) => interview.end_time >= convertToTimezone(now, interview.timezone ?? "UTC"),
  );
